import { z } from '@repo/schema'
import {
  EstimatedCostComparisonSchema,
  GetBillPreviewSchema,
  GetBillSchema,
  GetBillsTotalSchema,
  GetEstimatedCostSchema,
  PaymentMethodSchema,
  PaymentStatusSchema,
} from '@repo/schema/bill'
import { GetCurrencyCodeSchema } from '@repo/schema/currency'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import { GetPaginationSchema } from '@repo/schema/pagination'
import { GetUserSchema } from '@repo/schema/user'
import { UpFetch } from '@repo/upfetch'

import { createProcedure } from '../../lib/procedure'

// Bills have no manual payment status: whether a bill is paid comes from Xero.
const paymentInput = z.object({
  payment_method: PaymentMethodSchema.nullish(),
  planned_payment_date: z.iso.date().nullish(),
})

const estimatedCostPaymentInput = paymentInput.extend({
  payment_status: PaymentStatusSchema.nullish(),
})

// Create and update replace the whole record, so a field left out is cleared.
const estimatedCostInput = estimatedCostPaymentInput.extend({
  amount: z.number(),
  category: z.string(),
  currency: z.string(),
  notes: z.string().nullish(),
})

// Payment tags are written apart from the record: the full list of slugs replaces the old one.
const paymentTagsInput = z.object({ payment_tags: z.array(z.string()) })

// Left out on connect, the API tags the bill with the estimated cost's tags plus VIP for a VIP
// company; sent, even empty, the bill gets exactly these.
const connectPaymentTags = z.array(z.string()).optional()

const GetBillWithUserSchema = GetBillSchema.extend({
  user: GetUserSchema.nullish(),
})

type GetBillWithUser = z.infer<typeof GetBillWithUserSchema>

export const createBillsRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)

  return {
    carrierRate: {
      get: p
        .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'carrier-rate'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            // A load without a carrier rate answers `{ data: null }`, which the shared
            // data wrapper rejects, so unwrap it here.
            schema: z
              .object({ data: GetEstimatedCostSchema.nullable() })
              .transform(result => result.data),
          }),
        ),
      remove: p
        .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'carrier-rate'])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
      set: p
        .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'carrier-rate'])
        .input(
          // Omitted notes and payment fields stay untouched; null clears them.
          estimatedCostPaymentInput.extend({
            amount: z.number().int().positive(),
            currency: GetCurrencyCodeSchema,
            notes: z.string().nullish(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PUT',
            schema: getDataWrapperSchema(GetEstimatedCostSchema),
          }),
        ),
    },
    create: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'bills'])
      .input(
        paymentInput.extend({
          category: z.string(),
          line_item_ids: z.array(z.string()).nullish(),
          notes: z.string().nullish(),
          payment_tags: connectPaymentTags,
          url: z.string(),
        }),
      )
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetBillSchema),
        }),
      ),
    delete: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'bills',
        p.id,
      ])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
    estimatedCosts: {
      compareBill: p
        .path(z.object({ id: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'estimated-costs',
          p.id,
          'compare-bill',
        ])
        .input(
          z.object({
            line_item_ids: z.array(z.string()).nullish(),
            url: z.string(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(EstimatedCostComparisonSchema),
          }),
        ),
      connectBill: p
        .path(z.object({ id: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'estimated-costs',
          p.id,
          'connect-bill',
        ])
        // Omitted payment fields inherit the estimated cost's; null clears them.
        .input(
          paymentInput.extend({
            category: z.string(),
            line_item_ids: z.array(z.string()).nullish(),
            notes: z.string().nullish(),
            payment_tags: connectPaymentTags,
            url: z.string(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetBillSchema),
          }),
        ),
      create: p
        .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'estimated-costs'])
        .input(estimatedCostInput)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetEstimatedCostSchema),
          }),
        ),
      list: p
        .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'estimated-costs'])
        .query(async ({ path, upFetch }) => {
          const costs: z.infer<typeof GetEstimatedCostSchema>[] = []
          const schema = GetPaginationSchema(GetEstimatedCostSchema)
          let page = 1
          let lastPage = 1
          let perPage: number | undefined
          let total: null | number | undefined

          do {
            const result = await upFetch(path, { params: { page }, schema })
            const { current_page, last_page, per_page, total: pageTotal } = result.meta
            if (
              !Number.isSafeInteger(current_page) ||
              current_page !== page ||
              !Number.isSafeInteger(last_page) ||
              last_page < current_page ||
              (page > 1 && last_page !== lastPage) ||
              !Number.isSafeInteger(per_page) ||
              per_page < 1 ||
              (perPage !== undefined && per_page !== perPage) ||
              result.data.length > per_page ||
              (page > 1 && result.data.length === 0) ||
              (current_page < last_page && result.data.length !== per_page) ||
              (pageTotal != null && (!Number.isSafeInteger(pageTotal) || pageTotal < 0)) ||
              (page > 1 && pageTotal !== total) ||
              (pageTotal != null && last_page !== Math.max(1, Math.ceil(pageTotal / per_page)))
            ) {
              throw new Error('Invalid estimated-cost pagination')
            }
            costs.push(...result.data)
            lastPage = last_page
            perPage = per_page
            total = pageTotal
            page++
          } while (page <= lastPage)

          if (total != null && costs.length !== total) {
            throw new Error('Invalid estimated-cost pagination')
          }

          return costs
        }),
      remove: p
        .path(z.object({ id: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'estimated-costs',
          p.id,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
      // An estimated cost with a connected bill is refused: tag the bill instead.
      syncPaymentTags: p
        .path(z.object({ id: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'estimated-costs',
          p.id,
          'payment-tags',
        ])
        .input(paymentTagsInput)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PUT',
            schema: getDataWrapperSchema(GetEstimatedCostSchema),
          }),
        ),
      update: p
        .path(z.object({ id: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'estimated-costs',
          p.id,
        ])
        .input(estimatedCostInput)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PUT',
            schema: getDataWrapperSchema(GetEstimatedCostSchema),
          }),
        ),
    },
    getPreview: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'bills', 'preview'])
      // An edited bill is previewed in the Xero organisation it was connected from.
      .input(z.object({ organisation_id: z.number().nullish(), url: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetBillPreviewSchema),
        }),
      ),
    list: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'bills'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          params: {
            i: 'user',
          },
          schema: getDataWrapperSchema(GetBillWithUserSchema.array()).transform(bills =>
            bills.reduce<Record<string, GetBillWithUser[]>>((categories, bill) => {
              const alias = bill.category?.alias || 'other'
              categories[alias] ??= []
              categories[alias].push(bill)
              return categories
            }, {}),
          ),
        }),
      ),
    syncPaymentTags: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'bills',
        p.id,
        'payment-tags',
      ])
      .input(paymentTagsInput)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PUT',
          schema: getDataWrapperSchema(GetBillSchema),
        }),
      ),
    total: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'bills', 'total'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          schema: getDataWrapperSchema(GetBillsTotalSchema),
        }),
      ),
    // Replaces the whole record, so notes and payment fields left out are cleared. Only a changed
    // line selection is re-fetched from Xero.
    update: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'bills',
        p.id,
      ])
      .input(
        paymentInput.extend({
          category: z.string(),
          line_item_ids: z.array(z.string()).min(1),
          notes: z.string().nullable(),
        }),
      )
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PUT',
          schema: getDataWrapperSchema(GetBillSchema),
        }),
      ),
  }
}
